import { create } from 'zustand';
import api from '../services/api';

const useCustomStore = create((set, get) => ({
    // Thématiques list (dashboard level)
    thematiques: [],
    sharedThematiques: [],
    thematiquesQuota: null,
    thematiquesLoading: false,

    // Active thématique context (when inside a thématique)
    activeThematiqueId: null,
    sousSujets: [],
    categories: [],
    quota: null,
    loading: false,
    uploading: false,
    activeJob: null,
    pollTimer: null,

    // Cible enrichissement : { id, label } ou null
    // Set par MaThematiqueGenerations quand on clic "Compléter ce sujet" sur une carte,
    // lu par MaThematiqueUpload pour passer en mode enrichissement.
    enrichmentTarget: null,
    setEnrichmentTarget: (target) => set({ enrichmentTarget: target }),
    clearEnrichmentTarget: () => set({ enrichmentTarget: null }),

    // Pré-remplissage du formulaire "Génération IA" : { label, consignes } ou null.
    // Set par MaThematiqueGenerations ("Modifier & régénérer" sur un sujet IA prompt),
    // lu par MaThematiqueUpload qui bascule sur l'onglet Ajouter + mode ai-prompt.
    promptPrefill: null,
    setPromptPrefill: (prefill) => set({ promptPrefill: prefill }),
    clearPromptPrefill: () => set({ promptPrefill: null }),

    // ====== Thématiques (dashboard) ======

    fetchThematiques: async () => {
        set({ thematiquesLoading: true });
        try {
            const { data } = await api.get('/custom/thematiques.php');
            if (data.success) {
                set({
                    thematiques: data.data.thematiques || [],
                    sharedThematiques: data.data.shared_thematiques || [],
                    thematiquesQuota: data.data.quota || null,
                });
            }
        } catch (e) {
            console.error('fetchThematiques error:', e);
        } finally {
            set({ thematiquesLoading: false });
        }
    },

    createThematique: async (label, description = '', subscriptionId = null) => {
        const payload = { label, description };
        if (subscriptionId) payload.subscription_id = subscriptionId;
        const { data } = await api.post('/custom/thematiques.php', payload);
        if (data.success) {
            get().fetchThematiques();
            return data.data;
        }
        throw new Error(data.error || 'Erreur création');
    },

    updateThematique: async (id, updates) => {
        const { data } = await api.put('/custom/thematiques.php', { id, ...updates });
        if (data.success) {
            // Re-fetch to get server-side values (e.g. prefixed label)
            await get().fetchThematiques();
            return true;
        }
        return false;
    },

    deleteThematique: async (id) => {
        const { data } = await api.delete(`/custom/thematiques.php?id=${id}`);
        if (data.success) {
            get().fetchThematiques();
            return true;
        }
        return false;
    },

    // ====== Active thématique content ======

    setActiveThematique: (thematiqueId) => {
        set({ activeThematiqueId: thematiqueId, sousSujets: [], categories: [] });
    },

    fetchList: async (thematiqueId) => {
        const tid = thematiqueId || get().activeThematiqueId;
        if (!tid) return;
        set({ loading: true });
        try {
            const { data } = await api.get(`/custom/list.php?thematique_id=${tid}`);
            if (data.success) {
                set({
                    sousSujets: data.data.sous_sujets || [],
                    categories: data.data.categories || [],
                    quota: data.data.quota || null,
                });
            }
        } catch (e) {
            console.error('fetchList error:', e);
        } finally {
            set({ loading: false });
        }
    },

    uploadPdf: async (file, label, thematiqueId, provider = '', categoryId = null, force = false, targetParentUcssId = null, nbQuestions = 20) => {
        set({ uploading: true });
        try {
            const formData = new FormData();
            formData.append('document', file);
            // En mode enrichissement, label/category sont récupérés du parent côté backend
            if (!targetParentUcssId) {
                formData.append('label', label);
                if (categoryId) formData.append('user_category_id', String(categoryId));
            }
            formData.append('thematique_id', String(thematiqueId));
            formData.append('nb_questions', String(nbQuestions));
            if (provider) formData.append('provider', provider);
            if (force) formData.append('force', '1');
            if (targetParentUcssId) formData.append('target_parent_ucss_id', String(targetParentUcssId));

            const { data } = await api.post('/custom/upload.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (data.success) {
                get().startPolling(data.data.job_id, thematiqueId);
                get().fetchList(thematiqueId);
                return data.data;
            } else {
                throw new Error(data.error || 'Erreur upload');
            }
        } catch (e) {
            const msg = e.response?.data?.message || e.response?.data?.error || e.message || 'Erreur upload';
            const canRetry = e.response?.data?.errors?.can_retry || false;
            const err = new Error(msg);
            err.canRetry = canRetry;
            throw err;
        } finally {
            set({ uploading: false });
        }
    },

    resubmitJob: async (customSsId, thematiqueId) => {
        const { data } = await api.post('/custom/resubmit.php', { custom_ss_id: customSsId });
        if (data.success) {
            get().startPolling(data.data.job_id, thematiqueId);
            get().fetchList(thematiqueId);
            return data.data;
        }
        throw new Error(data.message || 'Erreur');
    },

    // Oublie le job async précédent (bannière COMPLETED/FAILED périmée).
    // Appelé quand on démarre une génération synchrone (prompt) pour que la
    // bannière de succès reflète bien la DERNIÈRE génération.
    clearActiveJob: () => {
        get().stopPolling();
        set({ activeJob: null });
    },

    // Poll job status
    startPolling: (jobId, thematiqueId) => {
        const existing = get().pollTimer;
        if (existing) clearInterval(existing);
        // Purge l'éventuel job précédent : sans ça, la bannière du job d'avant
        // (COMPLETED) reste affichée jusqu'à la première réponse du poll.
        set({ activeJob: null });

        const poll = async () => {
            try {
                const { data } = await api.get(`/custom/job-status.php?id=${jobId}`);
                if (data.success) {
                    const job = data.data;
                    set({ activeJob: job });

                    if (job.status === 'COMPLETED' || job.status === 'FAILED') {
                        get().stopPolling();
                        get().fetchList(thematiqueId);
                    }
                }
            } catch (e) {
                console.error('Poll error:', e);
            }
        };

        poll();
        const timer = setInterval(poll, 3000);
        set({ pollTimer: timer });
    },

    stopPolling: () => {
        const timer = get().pollTimer;
        if (timer) {
            clearInterval(timer);
            set({ pollTimer: null });
        }
    },

    deleteSousSujet: async (customSsId) => {
        try {
            const { data } = await api.delete(`/custom/delete.php?id=${customSsId}`);
            if (data.success) {
                get().fetchList();
                return true;
            }
        } catch (e) {
            console.error('Delete error:', e);
        }
        return false;
    },

    // ====== Category actions (scoped by thematique) ======

    createCategory: async (label, thematiqueId) => {
        const tid = thematiqueId || get().activeThematiqueId;
        const { data } = await api.post('/custom/categories.php', { label, thematique_id: tid });
        if (data.success) {
            get().fetchList(tid);
            return data.data;
        }
        throw new Error(data.error || 'Erreur création catégorie');
    },

    renameCategory: async (catId, label) => {
        const { data } = await api.put('/custom/categories.php', { id: catId, label });
        if (data.success) {
            set({ categories: get().categories.map(c => c.id === catId ? { ...c, label } : c) });
            return true;
        }
        return false;
    },

    deleteCategory: async (catId) => {
        const { data } = await api.delete(`/custom/categories.php?id=${catId}`);
        if (data.success) {
            get().fetchList();
            return true;
        }
        return false;
    },

    renameSousSujet: async (customSsId, newLabel) => {
        const { data } = await api.put('/custom/categories.php', {
            rename_sous_sujet: customSsId,
            label: newLabel,
        });
        if (data.success) {
            set({ sousSujets: get().sousSujets.map(s =>
                s.id === customSsId ? { ...s, sous_sujet_label: newLabel } : s
            ) });
            return true;
        }
        return false;
    },

    moveSousSujet: async (customSsId, toCategoryId) => {
        const { data } = await api.put('/custom/categories.php', {
            move_sous_sujet: customSsId,
            to_category: toCategoryId,
        });
        if (data.success) {
            set({ sousSujets: get().sousSujets.map(s =>
                s.id === customSsId ? { ...s, user_category_id: toCategoryId } : s
            ) });
            return true;
        }
        return false;
    },

    reorderCategories: async (thematiqueId, orderedItems) => {
        // orderedItems: [{ id: catId, ordre: 0 }, ...]
        try {
            await api.put('/custom/categories.php', {
                thematique_id: thematiqueId,
                reorder: orderedItems,
            });
            return true;
        } catch { return false; }
    },

    reorderSousSujets: async (thematiqueId, orderedItems) => {
        // orderedItems: [{ id: customSsId, ordre: 0 }, { id: customSsId, ordre: 1 }, ...]
        try {
            await api.put('/custom/categories.php', {
                thematique_id: thematiqueId,
                reorder_sous_sujets: orderedItems,
            });
            return true;
        } catch { return false; }
    },
}));

export default useCustomStore;
